import { z } from 'zod';
import type { IntlShape } from 'react-intl';

export const createPersonalAddressSchema = (intl: IntlShape) =>
  z.object({
    addressLine1: z
      .string()
      .trim()
      .min(1, intl.formatMessage({ id: 'customer-registration-kit.validation.address.required' }))
      .min(
        2,
        intl.formatMessage(
          { id: 'customer-registration-kit.validation.address.minLength' },
          { minLength: 2 }
        )
      ),
    addressLine2: z.string().optional(),
    addressZipcode: z
      .string()
      .trim()
      .min(1, intl.formatMessage({ id: 'customer-registration-kit.validation.zipcode.required' }))
      .min(
        3,
        intl.formatMessage(
          { id: 'customer-registration-kit.validation.zipcode.minLength' },
          { minLength: 3 }
        )
      )
      .max(
        10,
        intl.formatMessage(
          { id: 'customer-registration-kit.validation.zipcode.maxLength' },
          { maxLength: 10 }
        )
      ),
    addressCity: z
      .string()
      .trim()
      .min(1, intl.formatMessage({ id: 'customer-registration-kit.validation.city.required' })),
    addressCountry: z
      .string()
      .trim()
      .min(1, intl.formatMessage({ id: 'customer-registration-kit.validation.country.required' })),
  });

export const personalAddressSchema = z.object({
  addressLine1: z
    .string()
    .trim()
    .min(1, 'Address is required')
    .min(2, 'Address must be at least 2 characters'),
  addressLine2: z.string().optional(),
  addressZipcode: z
    .string()
    .trim()
    .min(1, 'Zip code is required')
    .min(3, 'Zip code must be between 3 and 10 characters')
    .max(10, 'Zip code must be between 3 and 10 characters'),
  addressCity: z.string().trim().min(1, 'City is required'),
  addressCountry: z.string().trim().min(1, 'Country is required'),
});

export type PersonalAddressSchema = z.infer<typeof personalAddressSchema>;

export function validatePersonalAddressWithZod(
  data: unknown,
  intl?: IntlShape
): {
  success: boolean;
  errors: Record<string, string>;
  data?: PersonalAddressSchema;
} {
  const schema = intl ? createPersonalAddressSchema(intl) : personalAddressSchema;

  try {
    const validData = schema.parse(data);
    return {
      success: true,
      errors: {},
      data: validData,
    };
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors: Record<string, string> = {};
      error.issues.forEach(issue => {
        if (issue.path.length > 0 && issue.path[0]) {
          errors[issue.path[0] as string] = issue.message;
        } else {
          errors.general = issue.message;
        }
      });
      return {
        success: false,
        errors,
      };
    }
    return {
      success: false,
      errors: {
        general: intl
          ? intl.formatMessage({ id: 'customer-registration-kit.validation.general.failed' })
          : 'Validation failed',
      },
    };
  }
}
